const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/g;

const NAME_INVALID = /[^\p{L}\p{M} .'’-]/gu;

const FREE_TEXT_INVALID = /[^\p{L}\p{N}\p{M} ,.'’#/&-]/gu;

export function stripControlChars(value: string): string {
  return value.replace(CONTROL_CHARS, "");
}

export function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

export function sanitizeName(value: string): string {
  return collapseWhitespace(stripControlChars(value).replace(NAME_INVALID, ""));
}

export function sanitizeFreeText(value: string): string {
  return collapseWhitespace(
    stripControlChars(value).replace(FREE_TEXT_INVALID, "")
  );
}

export function sanitizeEmail(value: string): string {
  return stripControlChars(value).replace(/\s+/g, "").trim();
}

export function sanitizePhoneInput(value: string): string {
  return stripControlChars(value)
    .replace(/[^\d+\s()-]/g, "")
    .slice(0, 15);
}

export function sanitizeNigerianPhone(value: string): string {
  let digits = digitsOnly(value);
  if (digits.startsWith("234") && digits.length > 10) {
    digits = digits.slice(3);
  }
  return digits.replace(/^0+/, "").slice(0, 10);
}

const MAX_LENGTHS = {
  firstname: 50,
  lastname: 50,
  customerName: 100,
  email: 254,
  message: 1000,
  phone: 15,
  phoneNumber: 10,
  nin: 11,
  address: 100,
} as const;

const FIELD_SANITIZERS: Record<string, (value: string) => string> = {
  firstname: sanitizeName,
  lastname: sanitizeName,
  customerName: sanitizeName,
  email: sanitizeEmail,
  message: sanitizeFreeText,
  phone: sanitizePhoneInput,
  phoneNumber: sanitizeNigerianPhone,
  nin: digitsOnly,
  address: sanitizeFreeText,
};

export function sanitizeField(name: string, value: string): string {
  const sanitize = FIELD_SANITIZERS[name] ?? ((v: string) => v.trim());
  const cleaned = sanitize(value);
  const max = MAX_LENGTHS[name as keyof typeof MAX_LENGTHS];
  return max ? cleaned.slice(0, max) : cleaned;
}

export function sanitizeFormValues<T extends Record<string, string>>(
  values: T
): T {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      sanitizeField(key, value),
    ])
  ) as T;
}
